/**
 * Form "Agendar instalação" (SlideOverPanel).
 *
 * Auditoria 2026-08-07 (ALTA-29): a Ordem de Instalação era inalcançável —
 * `criarOi` não tinha caller e nenhum evento a criava, então a tela de
 * instalações ficava permanentemente vazia e a âncora INSTALACAO das
 * parcelas nunca disparava. Este é o ponto de entrada que faltava.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiTool } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { criarOi, listarEquipes } from "../../../services/logistica";
import { listarPedidos, obterPedido } from "../../../services/pedidos";
import {
  ErrorMsg,
  Field,
  Input,
  Select,
  Textarea,
} from "../../Orcamentos/styles";

// Instala-se o que já saiu da fábrica (ou está saindo).
const SITUACOES_INSTALAVEIS = ["EM_EXPEDICAO", "ENTREGUE"];

export default function NovaOiForm({ aberto, onFechar, onCriada }) {
  const { request } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [pedidoId, setPedidoId] = useState("");
  const [equipeId, setEquipeId] = useState("");
  const [data, setData] = useState("");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [endereco, setEndereco] = useState("");
  const [telefone, setTelefone] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [erro, setErro] = useState(null);

  const carregarOpcoes = useCallback(async () => {
    setErro(null);
    try {
      const todos = [];
      for (const situacao of SITUACOES_INSTALAVEIS) {
        const p = await listarPedidos(request, { situacao, pageSize: 200 });
        todos.push(...(Array.isArray(p) ? p : p?.results || []));
      }
      setPedidos(todos);
      const eq = await listarEquipes(request, { ativo: true });
      setEquipes(Array.isArray(eq) ? eq : eq?.results || []);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar pedidos/equipes.");
    }
  }, [request]);

  useEffect(() => {
    if (aberto) carregarOpcoes();
  }, [aberto, carregarOpcoes]);

  useEffect(() => {
    let cancelado = false;
    if (!pedidoId) return undefined;
    (async () => {
      try {
        const p = await obterPedido(request, pedidoId);
        if (cancelado) return;
        if (!endereco) setEndereco(p?.cliente_endereco || "");
        if (!telefone) setTelefone(p?.cliente_telefone || "");
      } catch {
        /* endereço é digitável — falha aqui não bloqueia o agendamento */
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoId, request]);

  const limpar = () => {
    setPedidoId(""); setEquipeId(""); setData(""); setInicio(""); setFim("");
    setEndereco(""); setTelefone(""); setObservacoes(""); setErro(null);
  };

  const salvar = async () => {
    setErro(null);
    const exigir = (cond, msg) => {
      if (cond) return;
      const e = new Error(msg);
      setErro(msg);
      throw e;
    };
    exigir(pedidoId, "Escolha o pedido.");
    exigir(equipeId, "Escolha a equipe de instalação.");
    exigir(data, "Informe a data agendada.");
    exigir(endereco.trim(), "Endereço é obrigatório.");
    const oi = await criarOi(request, {
      pedido_id: pedidoId,
      equipe_id: equipeId,
      data_agendada: data,
      janela_inicio: inicio || undefined,
      janela_fim: fim || undefined,
      endereco: endereco.trim(),
      telefone_contato: telefone.trim(),
      observacoes: observacoes.trim(),
    });
    limpar();
    if (onCriada) await onCriada(oi);
    if (onFechar) onFechar();
  };

  return (
    <SlideOverPanel
      open={aberto}
      title="Agendar instalação"
      subtitle="A instalação acontece depois da entrega e pode ter data própria."
      onClose={() => { limpar(); if (onFechar) onFechar(); }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {erro && <ErrorMsg>{erro}</ErrorMsg>}

        <Field>
          <span style={{ fontWeight: 600 }}>Pedido</span>
          <Select value={pedidoId} onChange={(e) => setPedidoId(e.target.value)}>
            <option value="">— selecione —</option>
            {pedidos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.numero} · {p.cliente_nome || "cliente"} ({p.situacao_display || p.situacao})
              </option>
            ))}
          </Select>
          <span className="tiny muted">Pedidos em expedição ou já entregues.</span>
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Equipe</span>
          <Select value={equipeId} onChange={(e) => setEquipeId(e.target.value)}>
            <option value="">— selecione —</option>
            {equipes.map((eq) => (
              <option key={eq.id} value={eq.id}>
                {eq.nome}{eq.lider_nome ? ` · ${eq.lider_nome}` : ""}
              </option>
            ))}
          </Select>
          {!equipes.length && (
            <span className="tiny muted">
              Nenhuma equipe cadastrada — crie em Logística › Cadastros.
            </span>
          )}
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Data agendada</span>
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </Field>

        <div style={{ display: "flex", gap: 10 }}>
          <Field style={{ flex: 1 }}>
            <span style={{ fontWeight: 600 }}>Janela — início</span>
            <Input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </Field>
          <Field style={{ flex: 1 }}>
            <span style={{ fontWeight: 600 }}>Janela — fim</span>
            <Input type="time" value={fim} onChange={(e) => setFim(e.target.value)} />
          </Field>
        </div>

        <Field>
          <span style={{ fontWeight: 600 }}>Endereço</span>
          <Input
            value={endereco}
            onChange={(e) => setEndereco(e.target.value)}
            placeholder="Rua, número, complemento — cidade/UF"
          />
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Telefone de contato</span>
          <Input
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="(11) 90000-0000"
          />
        </Field>

        <Field>
          <span style={{ fontWeight: 600 }}>Observações</span>
          <Textarea
            rows={3}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Combinações com o cliente, acesso ao prédio, ferramentas…"
          />
        </Field>

        <AsyncButton
          variant="primary"
          onClick={salvar}
          successMessage="Instalação agendada."
          silentError
        >
          <FiTool size={14} /> Agendar
        </AsyncButton>
      </div>
    </SlideOverPanel>
  );
}
